import camelcase from 'camelcase';

import CurrencyField from '../pages/Builder/BuilderBody/BuilderTable/ColumnFields/CurrencyFieldEditable/CurrencyFieldEditable.jsx';
import NumberFieldEditable from '../pages/Builder/BuilderBody/BuilderTable/ColumnFields/NumberFieldEditable/NumberFieldEditable.jsx';
import PercentFieldEditable from '../pages/Builder/BuilderBody/BuilderTable/ColumnFields/PercentFieldEditable/PercentFieldEditable.jsx';
import SelectCurrencyField from '../pages/Builder/BuilderBody/BuilderTable/ColumnFields/SelectCurrencyValue/SelectCurrencyValue.jsx';
import SelectNumberValue from '../pages/Builder/BuilderBody/BuilderTable/ColumnFields/SelectNumberValue/SelectNumberValue.jsx';
import SelectPercentValue from '../pages/Builder/BuilderBody/BuilderTable/ColumnFields/SelectPercentValue/SelectPercentValue.jsx';
import SelectTextValue from '../pages/Builder/BuilderBody/BuilderTable/ColumnFields/SelectTextValue/SelectTextValue.jsx';
import TextValueField from '../pages/Builder/BuilderBody/BuilderTable/ColumnFields/TextValueField.jsx';
import {
  BuilderTableViewColumn,
  CustomColumnFormat,
  CustomColumnItem,
  CustomColumnType,
} from '../types/builderTableView.js';
import { builderColumnsObjects } from './builderColumnObjects.js';
import { DECIMAL_LIMIT, DEFAULT_FIELD_WIDTH } from './campaignBuilder.js';
import { Columns } from './myCampaigns.js';

const mapTypeAndFormatToCustomField = (
  column: CustomColumnItem,
  width: number,
) => ({
  [CustomColumnType.select]: {
    [CustomColumnFormat.currency]: {
      id: column.name,
      Header: column.label,
      accessor: column.name,
      Cell: SelectCurrencyField,
      options: column.selectValues.map((option) => ({
        ...option,
        displayName: option.label,
      })),
      decimals: DECIMAL_LIMIT.BUDGET,
      onlyTileEditable: true,
      width: width,
      hideColumnForNonFinancialUser: true,
      isCurrency: true,
    },
    [CustomColumnFormat.number]: {
      id: column.name,
      Header: column.label,
      accessor: column.name,
      Cell: SelectNumberValue,
      options: column.selectValues.map((option) => ({
        ...option,
        displayName: option.label,
      })),
      decimals: DECIMAL_LIMIT.BUDGET,
      onlyTileEditable: true,
      width: width,
    },
    [CustomColumnFormat.integer]: {
      id: column.name,
      Header: column.label,
      accessor: column.name,
      Cell: SelectNumberValue,
      options: column.selectValues.map((option) => ({
        ...option,
        displayName: option.label,
      })),
      decimals: DECIMAL_LIMIT.INTEGER,
      width: width,
      onlyTileEditable: true,
    },
    [CustomColumnFormat.percentage]: {
      id: column.name,
      Header: column.label,
      accessor: column.name,
      Cell: SelectPercentValue,
      options: column.selectValues.map((option) => ({
        ...option,
        displayName: option.label,
      })),
      decimals: DECIMAL_LIMIT.PERCENT,
      width: width,
      isPercent: true,
      onlyTileEditable: true,
    },

    [CustomColumnFormat.text]: {
      id: column.name,
      Header: column.label,
      accessor: column.name,
      Cell: SelectTextValue,
      options: column.selectValues.map((option) => ({
        ...option,
        displayName: option.label,
      })),
      width: width,
      onlyTileEditable: true,
    },
  },
  [CustomColumnType.singleInput]: {
    [CustomColumnFormat.currency]: {
      id: column.name,
      accessor: column.name,
      Header: column.label,
      Cell: CurrencyField,
      isCurrency: true,
      hideCurrencyForEmptyValue: true,
      hideColumnForNonFinancialUser: true,
      onlyTileEditable: true,
      decimals: DECIMAL_LIMIT.BUDGET,
      width: width,
    },
    [CustomColumnFormat.number]: {
      id: column.name,
      accessor: column.name,
      Header: column.label,
      Cell: NumberFieldEditable,
      onlyTileEditable: true,
      decimals: DECIMAL_LIMIT.BUDGET,
      width: width,
    },
    [CustomColumnFormat.integer]: {
      id: column.name,
      accessor: column.name,
      Header: column.label,
      Cell: NumberFieldEditable,
      onlyTileEditable: true,
      decimals: DECIMAL_LIMIT.INTEGER,
      width: width,
    },
    [CustomColumnFormat.percentage]: {
      id: column.name,
      accessor: column.name,
      Header: column.label,
      Cell: PercentFieldEditable,
      decimals: DECIMAL_LIMIT.PERCENT,
      isPercentField: true,
      width: width,
      onlyTileEditable: true,
    },
    [CustomColumnFormat.text]: {
      id: column.name,
      accessor: column.name,
      Header: column.label,
      Cell: TextValueField,
      width: width,
      onlyTileEditable: true,
    },
  },
});

export const getBuilderColumnObjects = (
  columns: BuilderTableViewColumn[],
  columnWidths?: Record<string, number>,
) => {
  const columnList = columns.map((col) => {
    const name = camelcase(col.name);
    const columnConfig = builderColumnsObjects[name];
    const isActionsMenu = name === Columns.ACTIONS_MENU;
    const isCustomTechCost = !columnConfig && 'isOtherTechCostField' in col;
    const isCustomColumn = !columnConfig && 'fieldType' in col;

    const widthOptions =
      columnWidths?.[name] ??
      builderColumnsObjects[name]?.width ??
      DEFAULT_FIELD_WIDTH;

    const columnWidth = isActionsMenu ? 0 : widthOptions;

    if (isCustomTechCost) {
      return {
        ...builderColumnsObjects.otherTechCost,
        id: name,
        accessor: name,
        Header: col.label,
        width: DEFAULT_FIELD_WIDTH,
      };
    }

    if (columnConfig) {
      return {
        ...columnConfig,
        width: columnWidth,
        Header: col.label,
      };
    }

    if (isCustomColumn) {
      return mapTypeAndFormatToCustomField(
        col,
        columnWidths?.[String(col.name)] ?? DEFAULT_FIELD_WIDTH,
      )[col.fieldType][col.fieldFormat];
    }

    return null;
  });

  // Filtering out columns that don't fit the conditions above.
  // It's related to the columns from the response schema that aren't in the builderColumnsObjects still.
  return columnList.filter((column) => !!column);
};
